<template>
  <section
    class="rounded-lg border border-gray-200 bg-white p-5"
    data-testid="step-create-organization"
  >
    <div class="flex flex-col items-center justify-center gap-6 py-4 md:flex-row lg:py-8">
      <div
        class="relative aspect-[264/180] w-full max-w-52 shrink-0 md:max-w-60 lg:max-w-72 xl:max-w-80"
        aria-hidden="true"
      >
        <svg
          class="size-full"
          viewBox="0 0 264 180"
          fill="none"
        >
          <ellipse class="qs-node fill-gray-100" style="--d: .1s;" cx="130" cy="164" rx="76" ry="7" />
          <ellipse class="qs-node fill-gray-100" style="--d: .2s;" cx="232" cy="162" rx="20" ry="5" />
          <g class="qs-node" style="--d: .18s;">
            <rect class="fill-primary-900" x="40" y="24" width="184" height="128" rx="9" />
            <rect class="fill-white" x="40" y="40" width="184" height="112" rx="9" />
            <rect class="fill-white" x="40" y="40" width="184" height="10" />
          </g>
          <circle class="qs-node fill-red-500" style="--d: .34s;" cx="51" cy="32" r="2.6" />
          <circle class="qs-node fill-orange-400" style="--d: .39s;" cx="59" cy="32" r="2.6" />
          <circle class="qs-node fill-green-400" style="--d: .44s;" cx="67" cy="32" r="2.6" />
          <g class="qs-node" style="--d: .52s;">
            <circle class="fill-primary-100" cx="59" cy="66" r="10" />
            <circle class="fill-primary-700" cx="59" cy="63" r="3.4" />
            <path class="fill-primary-700" d="M53.4 72.6a5.9 5.9 0 0 1 11.2 0Z" />
          </g>
          <rect class="qs-node fill-primary-500" style="--d: .62s;" x="49" y="84" width="20" height="5" rx="2.5" />
          <rect class="qs-node fill-primary-200" style="--d: .68s;" x="49" y="93" width="20" height="5" rx="2.5" />
          <rect class="qs-node fill-primary-200" style="--d: .74s;" x="49" y="102" width="20" height="5" rx="2.5" />
          <path class="qs-draw stroke-gray-200" style="--len: 72; --d: .6s;" d="M84 60 V132" stroke-width="1.5" stroke-linecap="round" />
          <path class="qs-draw stroke-gray-200" style="--len: 124; --d: .7s;" d="M84 132 H208" stroke-width="1.5" stroke-linecap="round" />
          <rect class="qs-grow fill-primary-50" style="--d: .9s;" x="92" y="106" width="9" height="26" rx="2" />
          <rect class="qs-grow fill-primary-50" style="--d: .98s;" x="112" y="92" width="9" height="40" rx="2" />
          <rect class="qs-grow fill-primary-50" style="--d: 1.06s;" x="132" y="98" width="9" height="34" rx="2" />
          <rect class="qs-grow fill-primary-50" style="--d: 1.14s;" x="152" y="78" width="9" height="54" rx="2" />
          <rect class="qs-grow fill-primary-50" style="--d: 1.22s;" x="172" y="86" width="9" height="46" rx="2" />
          <rect class="qs-grow fill-primary-100" style="--d: 1.3s;" x="192" y="66" width="9" height="66" rx="2" />
          <path
            class="qs-draw stroke-primary-700" style="--len: 132; --d: 1.15s;"
            d="M96 112 L116 98 L136 104 L156 84 L176 92 L196 72"
            stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"
          />
          <path
            class="qs-draw stroke-primary-500" style="--len: 132; --d: 1.35s;"
            d="M96 122 L116 116 L136 120 L156 108 L176 112 L196 100"
            stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"
          />
          <path
            class="qs-draw stroke-green-400" style="--len: 132; --d: 1.55s;"
            d="M96 126 L116 124 L136 128 L156 122 L176 120 L196 116"
            stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
          />
          <circle class="qs-node fill-white stroke-primary-700" style="--d: 1.75s;" cx="196" cy="72" r="4" stroke-width="2.4" />
          <g class="qs-float" style="--d: .2s;">
            <g class="qs-node" style="--d: 1.5s;">
              <rect class="fill-primary-100" x="212" y="18" width="46" height="34" rx="6" />
              <rect class="fill-primary-700" x="218" y="25" width="26" height="4" rx="2" />
              <rect class="fill-primary-200" x="218" y="33" width="34" height="3" rx="1.5" />
              <rect class="fill-primary-200" x="218" y="40" width="30" height="3" rx="1.5" />
            </g>
          </g>
          <g class="qs-float" style="--d: 1.4s;">
            <g class="qs-node" style="--d: 1.62s;">
              <path class="fill-primary-900" d="M6 46h30a6 6 0 0 1 6 6v14a6 6 0 0 1-6 6H18l-7 7v-7H6a6 6 0 0 1-6-6V52a6 6 0 0 1 6-6Z" />
              <circle class="qs-blink fill-white" style="--d: 0s;" cx="14" cy="59" r="2.4" />
              <circle class="qs-blink fill-white" style="--d: .2s;" cx="21" cy="59" r="2.4" />
              <circle class="qs-blink fill-white" style="--d: .4s;" cx="28" cy="59" r="2.4" />
            </g>
          </g>
          <g class="qs-node" style="--d: 1.8s;">
            <path class="fill-green-400" d="M232 136c-9-3-13-11-12-20 8 1 13 8 12 20Z" />
            <path class="fill-green-500" d="M234 136c8-4 11-12 9-21-8 2-12 10-9 21Z" />
            <path class="stroke-green-700" d="M233 122v18" stroke-width="1.6" stroke-linecap="round" />
            <path class="fill-primary-900" d="M223 138h20l-2.4 16a2 2 0 0 1-2 1.8h-11.2a2 2 0 0 1-2-1.8L223 138Z" />
          </g>
        </svg>
      </div>
      <div class="min-w-0 max-w-[40ch] text-center md:text-left">
        <p class="text-base font-semibold text-gray-900 lg:text-lg">{{ t('title') }}</p>
        <p class="mt-1.5 text-sm text-gray-500 lg:text-md">{{ t('description') }}</p>
        <ZoneButton
          v-if="nextStepId"
          color="primary_alt"
          class="mt-4"
          data-testid="create-organization-start"
          @click="$emit('start')"
        >
          {{ t('start_with', { step: t(`step_names.${nextStepId}`) }) }}
        </ZoneButton>
        <ZoneButton
          v-else
          color="primary_alt"
          class="mt-4"
          :href="dashboardUrl"
          trailing-icon="arrow_forward"
          data-testid="create-organization-dashboard"
        >
          {{ t('go_to_dashboard') }}
        </ZoneButton>
      </div>
    </div>
  </section>
</template>

<script setup>
import { useI18n } from 'vue-i18n';
import ZoneButton from 'zone/components/base/Button.vue';

const { nextStepId = null } = defineProps({
  nextStepId: { type: String },
  dashboardUrl: { type: String, required: true },
});

defineEmits(['start']);

const { t } = useI18n();
</script>

<style scoped>
@keyframes node { from { opacity: 0; transform: scale(0.35); } to { opacity: 1; transform: scale(1); } }
@keyframes draw { from { stroke-dashoffset: var(--len, 80); } to { stroke-dashoffset: 0; } }
@keyframes grow { from { transform: scaleY(0); } to { transform: scaleY(1); } }
@keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-0.5rem); } }
@keyframes blink { 0%, 100% { opacity: 0.3; } 50% { opacity: 1; } }

.qs-node {
  transform-box: fill-box;
  transform-origin: center;
  animation: node 0.42s cubic-bezier(0.34, 1.56, 0.64, 1) var(--d, 0s) both;
}
.qs-draw { stroke-dasharray: var(--len, 80); animation: draw 0.45s ease-out var(--d, 0s) both; }
.qs-grow {
  transform-box: fill-box;
  transform-origin: bottom;
  animation: grow 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) var(--d, 0s) both;
}
.qs-float { animation: float 4.5s ease-in-out var(--d, 0s) infinite; }
.qs-blink { animation: blink 1.4s ease-in-out var(--d, 0s) infinite; }

@media (prefers-reduced-motion: reduce) {
  .qs-node, .qs-draw, .qs-grow, .qs-float, .qs-blink { animation: none; }
}
</style>

<i18n lang="json" src="~/components/experiments/0239_getting_started_checklist/steps.json"></i18n>

<i18n lang="json">
{
  "en": {
    "title": "Your organization is ready",
    "description": "You created your organization when you signed up. Everything below builds on it.",
    "start_with": "Start with: {step}"
  }
}
</i18n>
